import React, { useEffect, useState } from "react";

import { AVAILABLE } from "@/constants/event";
import { Button2 } from "@/ui2/atoms/Button";
import dayjs from "@/lib/helpers/dayjs";
import { eventTypeDisplayMini } from "@/components/Provider/ScheduleAvailability/ActiveScheduleDetails";
import { FormField, RadioGroup, RadioOld } from "@/component-library";
import { Select } from "@/components/UI/StyledInput";

import { RECURRING_AVAILABILITY_VALUE } from "./ScheduleDeleteSeriesModal";

const CONFLICT_SELF = "conflict_self";
const CONFLICT_MEMBER = "conflict_member";
const OTHER = "other";

const CONFLICT_SELF_LABEL = "I have a conflict with this time";
const CONFLICT_MEMBER_LABEL = "The member has a conflict with this time";
const OTHER_LABEL = "Other";

const VIDEO_CALL_RECURRING_TITLE = "Cancel Session?";

const SELECTED_SESSION = "selected_session";
const SELECTED_SESSION_LABEL = "This session";

const SELECTED_AND_REOCCURRING_SESSIONS = "selected_and_reoccurring_sessions";
const SELECTED_AND_REOCCURRING_SESSIONS_LABEL = "This and all reoccurring sessions";

const TWENTY_FOUR_NOTICE_YES = "yes";
const TWENTY_FOUR_NOTICE_NO = "no";

const TWENTY_FOUR_NOTICE_YES_LABEL = "Yes";
const TWENTY_FOUR_NOTICE_NO_LABEL = "No";

const DELETE_SERIES_OPTIONS = [
  { value: SELECTED_SESSION, display: SELECTED_SESSION_LABEL },
  { value: SELECTED_AND_REOCCURRING_SESSIONS, display: SELECTED_AND_REOCCURRING_SESSIONS_LABEL },
];

const REASON_OPTIONS = [
  { value: CONFLICT_SELF, display: CONFLICT_SELF_LABEL },
  { value: CONFLICT_MEMBER, display: CONFLICT_MEMBER_LABEL },
  { value: OTHER, display: OTHER_LABEL },
];

const TWENTY_FOUR_NOTICE_OPTIONS = [
  { value: TWENTY_FOUR_NOTICE_YES, display: TWENTY_FOUR_NOTICE_YES_LABEL },
  { value: TWENTY_FOUR_NOTICE_NO, display: TWENTY_FOUR_NOTICE_NO_LABEL },
];

const seriesRadioOptions = [
  {
    label: SELECTED_SESSION_LABEL,
    value: SELECTED_SESSION,
  },
  {
    label: SELECTED_AND_REOCCURRING_SESSIONS_LABEL,
    value: SELECTED_AND_REOCCURRING_SESSIONS,
  },
];

const CancelSeriesFormField = ({ type, deleteSeriesOnChange, deleteSeries }) => {
  return (
    <>
      <FormField label={VIDEO_CALL_RECURRING_TITLE}>
        <div className="ScheduleDeleteDialog__input-title--divider" />

        <RadioGroup
          className={`ScheduleDeleteDialog__rd${type === AVAILABLE ? "--availability" : ""}`}
          name="delete_series"
          onChange={deleteSeriesOnChange}
        >
          <RadioOld label={seriesRadioOptions[0].label} value={seriesRadioOptions[0].value} />
          <RadioOld label={seriesRadioOptions[1].label} value={seriesRadioOptions[1].value} />
        </RadioGroup>

        <div className="ScheduleDeleteDialog__sel">
          <Select
            changeHandler={deleteSeriesOnChange}
            className="ScheduleDeleteDialog__sel--select"
            options={DELETE_SERIES_OPTIONS}
            placeholder={{ display: "Select" }}
            value={deleteSeries}
          />
        </div>
      </FormField>
    </>
  );
};

export function ScheduleDeleteSessionModal({ onCancel, onSubmit, callInfo, type }) {
  const [cancellationReason, setCancellationReason] = useState(undefined);
  const [deleteSeries, setDeleteSeries] = useState(undefined);
  const [twentyFourNotice, setTwentyFourNotice] = useState(undefined);
  const [formComplete, setFormComplete] = useState(false);

  const { timestamp, event_type, patient_name, series_id } = callInfo;
  const dateTimeLocal = dayjs.utc(timestamp, "YYYY-MM-DD HH:mm").local();

  useEffect(() => {
    setFormComplete(isFormComplete());
  }, [cancellationReason, deleteSeries, twentyFourNotice]);

  const isFormComplete = () => {
    const deleteSeriesComplete = series_id ? deleteSeries : true;
    const cancellationReasonComplete =
      cancellationReason === CONFLICT_MEMBER
        ? cancellationReason && twentyFourNotice
        : cancellationReason;

    return deleteSeriesComplete && cancellationReasonComplete;
  };

  const reasonOnChange = (e) => {
    setCancellationReason(e.target.value);
    setTwentyFourNotice(undefined);
  };

  const deleteSeriesOnChange = (e) => {
    setDeleteSeries(e.target.value);
  };

  const twentyFourNoticeOnChange = (e) => {
    setTwentyFourNotice(e.target.value);
  };

  const scheduleDeleteHandler = () => {
    const shouldDeleteSeries =
      deleteSeries === SELECTED_AND_REOCCURRING_SESSIONS ||
      deleteSeries === RECURRING_AVAILABILITY_VALUE;

    onSubmit(cancellationReason, twentyFourNotice, shouldDeleteSeries);
  };

  const renderEventType = () => {
    return eventTypeDisplayMini(event_type, false);
  };

  // Removing the Fragment breaks radio logic
  const renderCancellationReasonFormField = () => {
    return (
      <>
        <FormField label="What is the reason for the cancellation?">
          <>
            <div className="ScheduleDeleteDialog__input-title--divider" />

            <RadioGroup
              className="ScheduleDeleteDialog__rd"
              name="cancellation_reason"
              onChange={reasonOnChange}
            >
              <RadioOld label={CONFLICT_SELF_LABEL} value={CONFLICT_SELF} />
              <RadioOld label={CONFLICT_MEMBER_LABEL} value={CONFLICT_MEMBER} />

              {render24HourNoticeFormField()}

              <RadioOld label={OTHER_LABEL} value={OTHER} />
            </RadioGroup>
            {render24HourNoticeSelect()}
          </>
        </FormField>
      </>
    );
  };

  // Removing the Fragment breaks radio logic
  const render24HourNoticeFormField = () => {
    return (
      <>
        <FormField
          className={`ScheduleDeleteDialog__rd--24${
            cancellationReason === CONFLICT_MEMBER ? " active" : ""
          }`}
          label="Did member provide 24 hours' notice?"
        >
          <RadioGroup name="twenty_four_notice" onChange={twentyFourNoticeOnChange}>
            <RadioOld label={TWENTY_FOUR_NOTICE_YES_LABEL} value={TWENTY_FOUR_NOTICE_YES} />
            <RadioOld label={TWENTY_FOUR_NOTICE_NO_LABEL} value={TWENTY_FOUR_NOTICE_NO} />
          </RadioGroup>
        </FormField>
      </>
    );
  };

  // Removing the Fragment breaks radio logic
  const render24HourNoticeSelect = () => {
    return (
      <>
        <div className="ScheduleDeleteDialog__sel">
          <Select
            changeHandler={reasonOnChange}
            className="ScheduleDeleteDialog__sel--select"
            options={REASON_OPTIONS}
            placeholder={{ display: "Select a reason" }}
            value={cancellationReason}
          />

          <div
            className={`ScheduleDeleteDialog__sel--24${
              cancellationReason === CONFLICT_MEMBER ? " active" : ""
            }`}
          >
            <h3 className="ScheduleDeleteDialog__input-title">
              Did member provide 24 hours' notice?
            </h3>

            <Select
              changeHandler={twentyFourNoticeOnChange}
              className="ScheduleDeleteDialog__sel--select"
              options={TWENTY_FOUR_NOTICE_OPTIONS}
              placeholder={{ display: "Select" }}
              value={twentyFourNotice}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="ScheduleDeleteDialog position-fixed d-flex align-items-center tw:justify-center">
      <div
        className={`ScheduleDeleteDialog__inner--vid${
          cancellationReason === CONFLICT_MEMBER ? " active" : ""
        } d-flex flex-column text-center position-absolute`}
      >
        <h2 className="ScheduleDeleteDialog__title">You are cancelling the following session:</h2>

        <p className="ScheduleDeleteDialog__info">
          {renderEventType()} with {patient_name}
          <br />
          {dateTimeLocal.format("MMMM D, YYYY h:mm a")}
        </p>

        {series_id && (
          <CancelSeriesFormField
            deleteSeries={deleteSeries}
            deleteSeriesOnChange={deleteSeriesOnChange}
            type={type}
          />
        )}

        {renderCancellationReasonFormField()}

        <div className="tw:grid tw:grid-flow-col tw:grid-cols-2 tw:gap-4">
          <Button2 onPress={onCancel} variant="secondary">
            Keep Session
          </Button2>

          <Button2 isDisabled={!formComplete} onPress={scheduleDeleteHandler}>
            Cancel Session
          </Button2>
        </div>
      </div>
    </div>
  );
}
